import { ActionCreators, ILedger, Ledger, Ledgers } from 'exchange-common'
import { ceil, minus, multipliedBy } from 'libs-tools'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Modal from 'react-responsive-modal'
import 'react-responsive-modal/styles.css'
import { useParams } from 'react-router'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { Utils } from '../functions/utils'
import { walletAmount, walletBalance, walletInOrders } from '../functions/wallet'
import { ws } from '../functions/ws'
import { IState } from '../models/state'

interface IMatchParams {
	userId: string
}

export const UserDetailsMain = () => {

	const user = useSelector((state: IState) => state.userDetails)
	const kyc = useSelector((state: IState) => state.kycDetails)
	const deposits = useSelector((state: IState) => state.userDeposits.list)
	const withdrawals = useSelector((state: IState) => state.userWithdrawals.list)
	const wallets = useSelector((state: IState) => state.userWallets.list)
	const openOrders = useSelector((state: IState) => state.userOpenOrders)
	const ordersHistory = useSelector((state: IState) => state.userOrdersHistory)
	const trades = useSelector((state: IState) => state.userTrades)
	const markets = useSelector((state: IState) => state.markets.list)

	const userId: number = +useParams<IMatchParams>().userId
	const [openReceiptModal, setOpenReceiptModal] = useState(false)
	const [depositId, setDepositId] = useState<number>()

	useEffect(() => {
		ws.send({ type: 'UserDetails.GetProfile', userId } as any)
		if (!Object.keys(markets).length) {
			ws.send(ActionCreators.Market.getList())
		}
	}, [])

	const profileAvatar = require('../images/avatar-bg.svg')

	const brlLogo = require('../images/BRL.svg')
	const linkLogo = require('../images/LINK.svg')
	const btcLogo = require('../images/bitcoin.svg')
	const ltcLogo = require('../images/litecoin.svg')
	const ethLogo = require('../images/ethereum.svg')
	const usdcLogo = require('../images/USDC.svg')
	const ebitLogo = require('../images/ebit.svg')

	if (!user || !Object.keys(markets).length) {
		return <div />
	}

	const tags: any = {
		unverified: 'tagPurple',
		pending: 'tagYellow',
		verified: 'tagGreen',
		blocked: 'tagRed',
		admin: 'tagBlack',
		operator: 'tagGrey'
	}
	const onTabChange = (index: number) => {
		switch (index) {
			case 0:
				ws.send({ type: 'UserDetails.GetProfile', userId } as any)
				break
			case 1:
				ws.send({ type: 'KycDetails.Get', userId } as any)
				break
			case 2:
				ws.send({ type: 'UserDeposits.GetList', userId } as any)
				break
			case 3:
				ws.send({ type: 'UserWithdrawals.GetList', userId } as any)
				break
			case 4:
				ws.send({ type: 'UserOpenOrders.GetList', userId } as any)
				ws.send({ type: 'UserOrdersHistory.GetList', userId } as any)
				break
			case 5:
				ws.send({ type: 'UserDeposits.GetList', userId } as any)
				ws.send({ type: 'UserWithdrawals.GetList', userId } as any)
				ws.send({ type: 'UserWallets.GetList', userId } as any)
				ws.send({ type: 'UserTrades.GetList', userId } as any)
				break
			case 6:
				ws.send({ type: 'UserWallets.GetList', userId } as any)
				break
			default:
		}
	}

	const verify = () => {
		if (confirm('Are you sure?')) {
			ws.send({ type: 'KycDetails.Verify', userId } as any)
		}
	}

	const clear = () => {
		if (confirm('Are you sure?')) {
			ws.send({ type: 'KycDetails.Clear', userId } as any)
		}
	}

	const block = () => {
		if (confirm('Are you sure?')) {
			ws.send({ type: 'KycDetails.Block', userId } as any)
		}
	}

	const pdfIcon = require('../images/pdf.svg')

	const openImg = (img: string) => {
		window.open()!.document.body.innerHTML = `<img src='${img}' />`
	}

	const onOpenReceiptModal = (id: number) => {
		setDepositId(id)
		const d = deposits.find(f => f.id === id)
		if (!d) {
			return
		}
		if (!d.receiptPic) {
			ws.send({ type: 'UserDeposits.GetImg', depositId: id } as any)
		}
		setOpenReceiptModal(true)
	}

	const onCloseReceiptModal = () => {
		console.log('onCloseModal')
		setOpenReceiptModal(false)
	}

	const deposit = deposits.find(f => f.id === depositId)

	const cancelOrder = (orderId: number) => {
		const confirmed = confirm(`do you really want to cancel orderId ${orderId}?`)
		if (!confirmed) {
			return
		}
		const cancelAction: any = { type: 'Order.CancelAdmin', order: { orderId } }
		ws.send(cancelAction)
	}

	const getDecimals = (coin: string) => wallets[coin].fiat ? 2 : 8

	const ledgers = new Ledgers()
	if (Object.keys(wallets).length && Array.isArray(trades) && Array.isArray(deposits) && Array.isArray(withdrawals)) {
		const ledgerWallets: { [coin: string]: number } = {}
		Object.keys(wallets).forEach(coin => {
			ledgerWallets[coin] = wallets[coin].amount
		})
		const depositsLedger = deposits.filter(f => f.status === 'Confirmed').map(m => new Ledger({
			id: `D${m.id}`,
			date: m.date,
			coin: m.coin,
			description: wallets[m.coin].fiat ? 'deposit_from_bank_account' : m.record === 'internal' ? 'deposit_from_internal_wallet' : 'deposit_from_external_wallet',
			value: m.amount,
			balance: 0,
		} as ILedger))
		ledgers.list.push(...depositsLedger)

		const withdrawsLedger: ILedger[] = []
		withdrawals.filter(f => f.status === 'sent').reduce(
			(res, cur) => {
				res.push(
					new Ledger({
						id: `W${cur.id}`,
						date: cur.date,
						coin: cur.coin,
						description: wallets[cur.coin].fiat ? 'withdraw_to_bank_account' : cur.txid === 'internal' ? 'withdraw_to_internal_wallet' : 'withdraw_to_external_wallet',
						value: -(minus(cur.amount, cur.fee)),
						balance: 0,
					} as ILedger))
				res.push(
					new Ledger({
						id: `WF${cur.id}`,
						date: cur.date,
						coin: cur.coin,
						description: 'withdraw_fee',
						value: -(cur.fee),
						balance: 0,
					} as ILedger))
				return res
			},
			withdrawsLedger)
		ledgers.list.push(...withdrawsLedger)

		const tradesLedger: ILedger[] = []
		trades.reduce(
			(res, cur) => {
				const otherCoin: string = cur.market.split('-')[0]
				const baseCoin: string = cur.market.split('-')[1]
				if (cur.buyerUserId === user.id) {
					res.push(
						new Ledger({
							id: `TB${cur.tradeId}`,
							date: cur.date,
							coin: baseCoin,
							description: `buy (${cur.side === 'buy' ? 'market_ledger' : 'limit_ledger'}) @ ${cur.price} ${baseCoin}`,
							value: -(ceil(multipliedBy(cur.amount, cur.price), getDecimals(baseCoin))),
							balance: 0,
						} as ILedger))
					res.push(
						new Ledger({
							id: `TS${cur.tradeId}`,
							date: cur.date,
							coin: otherCoin,
							description: `buy (${cur.side === 'buy' ? 'market_ledger' : 'limit_ledger'}) @ ${cur.price} ${baseCoin}`,
							value: cur.amount,
							balance: 0,
						} as ILedger))
					res.push(
						new Ledger({
							id: `TF${cur.tradeId}`,
							date: cur.date,
							coin: otherCoin,
							description: `trade_fee (${cur.side === 'buy' ? 'taker' : 'maker'})`,
							value: -cur.buyerFee,
							balance: 0,
						} as ILedger))
				}
				if (cur.sellerUserId === user.id) {
					res.push(
						new Ledger({
							id: `TS${cur.tradeId}`,
							date: cur.date,
							coin: otherCoin,
							description: `sell (${cur.side === 'sell' ? 'market_ledger' : 'limit_ledger'}) @ ${cur.price} ${baseCoin}`,
							value: -cur.amount,
							balance: 0,
						} as ILedger))
					res.push(
						new Ledger({
							id: `TB${cur.tradeId}`,
							date: cur.date,
							coin: baseCoin,
							description: `sell (${cur.side === 'sell' ? 'market_ledger' : 'limit_ledger'}) @ ${cur.price} ${baseCoin}`,
							value: ceil(multipliedBy(cur.amount, cur.price), getDecimals(baseCoin)),
							balance: 0,
						} as ILedger))
					res.push(
						new Ledger({
							id: `TF${cur.tradeId}`,
							date: cur.date,
							coin: baseCoin,
							description: `trade_fee (${cur.side === 'sell' ? 'taker' : 'maker'})`,
							value: -cur.sellerFee,
							balance: 0,
						} as ILedger))
				}
				return res
			},
			tradesLedger)
		ledgers.list.push(...tradesLedger)

		ledgers.list.sort((a, b) => a.date < b.date ? 1 : -1)
		ledgers.list.forEach(ledger => {
			ledger.balance = ledgerWallets[ledger.coin]
			ledgerWallets[ledger.coin] -= ledger.value
		})

	}

	return (
		<div className='ordersMain'>
			<span className='pageTitle'>User Details</span>
			<Modal
				open={openReceiptModal}
				onClose={onCloseReceiptModal}
				classNames={{
					overlay: 'customOverlay',
					modal: 'customModal',
				}}
				center={true}
			>
				<div className='modalForm'>
					<h2>Deposit Receipt</h2>
					<div className='modalFormImg'>
						{deposit?.receiptPic?.startsWith('data:image')
							?
							<img src={deposit.receiptPic} alt='deposit_receipt' />
							: deposit?.receiptPic
								?
								<a href={deposit.receiptPic} download='receipt.pdf'>
									<img src={pdfIcon} alt='deposit_receipt' />
								</a>
								:
								<div />
						}
					</div>
				</div>
			</Modal>
			<Tabs onSelect={onTabChange}>
				<TabList>
					<Tab>Profile</Tab>
					<Tab>KYC</Tab>
					<Tab>Deposits</Tab>
					<Tab>Withdrawals</Tab>
					<Tab>Orders</Tab>
					<Tab>Ledger</Tab>
					<Tab>Wallets</Tab>
				</TabList>
				<TabPanel>
					<span className='pageBoxTitle'>Profile Info</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem dateColumn'>Register Date</td>
									<td className='ordersTableHeaderRowItem idColumn'>User ID</td>
									<td className='ordersTableHeaderRowItem'>Email</td>
									<td className='ordersTableHeaderRowItem dateColumn'>Last seen</td>
									<td className='ordersTableHeaderRowItem'>Status</td>
								</tr>
							</thead>
							<tbody>
								<tr className='ordersTableBodyRow'>
									<td className='ordersTableBodyRowItem'>{user.registerDate ? new Date(user.registerDate).toLocaleString('en-GB') : ''}</td>
									<td className='ordersTableBodyRowItem'>{Utils.pad(Utils.base32.encode(user.id), 6)}</td>
									<td className='ordersTableBodyRowItem'>{user.email}</td>
									<td className='ordersTableBodyRowItem'>{user.lastSeen ? new Date(user.lastSeen).toLocaleString('en-GB') : ''}</td>
									<td className='ordersTableBodyRowItem'><span className={`profileFormTag ${tags[user.status]}`}>{user.status}</span></td>
								</tr>
							</tbody>
						</table>
					</div>
					<br />
					<span className='pageBoxTitle'>More Info</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem'>Avatar</td>
									<td className='ordersTableHeaderRowItem'>Nickname</td>
									<td className='ordersTableHeaderRowItem'>Mobile Phone</td>
									{/* <td className='ordersTableHeaderRowItem'>Language</td> */}
								</tr>
							</thead>
							<tbody>
								<tr className='ordersTableBodyRow'>
									<td className='ordersTableBodyRowItem'><img src={profileAvatar} className='miniAvatar' alt='Avatar' /></td>
									<td className='ordersTableBodyRowItem'>{user.nickname}</td>
									<td className='ordersTableBodyRowItem'>{user.mobilePhone}</td>
									{/* <td className='ordersTableBodyRowItem'>English</td> */}
								</tr>
							</tbody>
						</table>
					</div>
				</TabPanel>
				<TabPanel>
					<span className='pageBoxTitle'>Actions</span>
					<div className='pageColumn3Box'>
						<div className='pageColumn3'>
							<div className='kycSubmitButtonRed'>
								<button onClick={block} type='submit'>Block</button>
							</div>
						</div>
						<div className='pageColumn3'>
							<div className='kycSubmitButtonPurple'>
								<button onClick={clear} type='submit'>Unverify</button>
							</div>
						</div>
						<div className='pageColumn3'>
							<div className='kycSubmitButtonGreen'>
								<button onClick={verify} type='submit'>Verify</button>
							</div>
						</div>
					</div>
					<span className='pageBoxTitle'>Personal Data</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem'>Full Name</td>
									<td className='ordersTableHeaderRowItem'>Birth Date</td>
									<td className='ordersTableHeaderRowItem'>Account Type</td>
									<td className='ordersTableHeaderRowItem'>Tax ID Number</td>
								</tr>
							</thead>
							<tbody>
								{kyc ?
									<tr className='ordersTableBodyRow'>
										<td className='ordersTableBodyRowItem capitalize'>{kyc.fullName}</td>
										<td className='ordersTableBodyRowItem'>{kyc.birthdate ? new Date(kyc.birthdate).toLocaleString('en-GB').split(', ')[0] : ''}</td>
										<td className='ordersTableBodyRowItem capitalize'>{kyc.docTypeId}</td>
										<td className='ordersTableBodyRowItem'>{kyc.docIdNumber}</td>
									</tr>
									: <></>
								}
							</tbody>
						</table>
					</div>
					<br />
					<span className='pageBoxTitle'>Address Info</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem'>Street Address</td>
									<td className='ordersTableHeaderRowItem'>City</td>
									<td className='ordersTableHeaderRowItem'>State</td>
									<td className='ordersTableHeaderRowItem'>Postcode</td>
									<td className='ordersTableHeaderRowItem'>Country</td>
								</tr>
							</thead>
							<tbody>
								{kyc ?
									<tr className='ordersTableBodyRow'>
										<td className='ordersTableBodyRowItem capitalize'>{kyc.streetAddress}</td>
										<td className='ordersTableBodyRowItem capitalize'>{kyc.city}</td>
										<td className='ordersTableBodyRowItem capitalize'>{kyc.state}</td>
										<td className='ordersTableBodyRowItem'>{kyc.postcode}</td>
										<td className='ordersTableBodyRowItem'>{kyc.country}</td>
									</tr>
									: <></>
								}
							</tbody>
						</table>
					</div>
					<br />
					<span className='pageBoxTitle'>Uploads</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem'>Document ID Photo</td>
									<td className='ordersTableHeaderRowItem'>Selfie holding ID Photo</td>
									<td className='ordersTableHeaderRowItem'>Proof of Residence</td>
								</tr>
							</thead>
							<tbody>
								<tr className='ordersTableBodyRow'>
									<td className='ordersTableBodyRowItem photoColumn'>
										{!kyc?.docIdPic
											?
											<img src={profileAvatar} className='kycUpload' alt='Document ID Photo' />
											: kyc.docIdPic.startsWith('data:image')
												?
												<a href='#' onClick={() => openImg(kyc.docIdPic)}>
													<img src={kyc.docIdPic} className='kycUpload' alt='Document ID Photo' />
												</a>
												:
												<a href={kyc.docIdPic} download='docIdPic.pdf'>
													<img src={pdfIcon} className='kycUpload' alt='Document ID Photo' />
												</a>
										}
									</td>
									<td className='ordersTableBodyRowItem photoColumn'>
										{!kyc?.selfiePic
											?
											<img src={profileAvatar} className='kycUpload' alt='Selfie holding ID Photo' />
											: kyc.selfiePic.startsWith('data:image')
												?
												<a href='#' onClick={() => openImg(kyc.selfiePic)}>
													<img src={kyc.selfiePic} className='kycUpload' alt='Selfie holding ID Photo' />
												</a>
												:
												<a href={kyc.selfiePic} download='selfiePic.pdf'>
													<img src={pdfIcon} className='kycUpload' alt='Selfie holding ID Photo' />
												</a>
										}
									</td>
									<td className='ordersTableBodyRowItem photoColumn'>
										{!kyc?.proofResPic
											?
											<img src={profileAvatar} className='kycUpload' alt='Proof of Residence' />
											: kyc.proofResPic.startsWith('data:image')
												?
												<a href='#' onClick={() => openImg(kyc.proofResPic)}>
													<img src={kyc.proofResPic} className='kycUpload' alt='Proof of Residence' />
												</a>
												:
												<a href={kyc.proofResPic} download='proofResPic.pdf'>
													<img src={pdfIcon} className='kycUpload' alt='Proof of Residence' />
												</a>
										}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</TabPanel>
				<TabPanel>
					<span className='pageBoxTitle'>User Deposits</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem'>Deposit ID</td>
									<td className='ordersTableHeaderRowItem dateColumn'>Date</td>
									<td className='ordersTableHeaderRowItem'>Coin</td>
									<td className='ordersTableHeaderRowItem'>Amount</td>
									<td className='ordersTableHeaderRowItem'>Status</td>
									<td className='ordersTableHeaderRowItem'>Link</td>
								</tr>
							</thead>
							<tbody>
								{deposits
									?
									deposits.map(m =>
										<tr className='ordersTableBodyRow' key={m.id}>
											<td className='ordersTableBodyRowItem'>{Utils.pad(Utils.base32.encode(m.id), 6)}</td>
											<td className='ordersTableBodyRowItem'>{m.date ? new Date(m.date).toLocaleString('en-GB') : ''}</td>
											<td className='ordersTableBodyRowItem'>{m.coin}</td>
											<td className='ordersTableBodyRowItem'>{m.amount.toFixed('BRL' === m.coin ? 2 : 8)}</td>
											<td className='ordersTableBodyRowItem'>{m.status}</td>
											<td className='ordersTableBodyRowItem alignColumn'>
												{'BRL' === m.coin
													?
													<button className='walletsTableButton' onClick={() => onOpenReceiptModal(m.id)}>Receipt</button>
													:
													m.record === 'internal'
														?
														<span className='walletsTableButton internal'>Internal</span>
														:
														<a className='walletsTableButton' href={`${blockExplorer(m.coin)}${m.record}`} target='_blank' rel='noopener noreferrer'>Link</a>
												}
											</td>
										</tr>
									)
									:
									<></>
								}
							</tbody>
						</table>
					</div>
				</TabPanel>
				<TabPanel>
					<span className='pageBoxTitle'>User Withdrawals</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem'>Withdraw ID</td>
									<td className='ordersTableHeaderRowItem dateColumn'>Date</td>
									<td className='ordersTableHeaderRowItem'>Coin</td>
									<td className='ordersTableHeaderRowItem'>Amount</td>
									<td className='ordersTableHeaderRowItem'>Status</td>
									<td className='ordersTableHeaderRowItem'>Link</td>
								</tr>
							</thead>
							<tbody>
								{withdrawals
									?
									withdrawals.map(m =>
										<tr className='ordersTableBodyRow' key={m.id}>
											<td className='ordersTableBodyRowItem'>{Utils.pad(Utils.base32.encode(m.id), 6)}</td>
											<td className='ordersTableBodyRowItem'>{m.date ? new Date(m.date).toLocaleString('en-GB') : ''}</td>
											<td className='ordersTableBodyRowItem'>{m.coin}</td>
											<td className='ordersTableBodyRowItem'>{m.amount.toFixed('BRL' === m.coin ? 2 : 8)}</td>
											<td className='ordersTableBodyRowItem'>{m.status}</td>

											<td className='ordersTableBodyRowItem alignColumn'>
												{'BRL' === m.coin || !m.txid
													?
													<span />
													:
													m.txid === 'internal'
														?
														<span className='walletsTableButton internal'>Internal</span>
														:
														<a className='walletsTableButton' href={`${blockExplorer(m.coin)}${m.txid}`} target='_blank' rel='noopener noreferrer'>Link</a>
												}
											</td>
										</tr>
									)
									:
									<></>
								}
							</tbody>
						</table>
					</div>
				</TabPanel>
				<TabPanel>
					<span className='pageBoxTitle'>Open Orders</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem idColumn'>Order ID</td>
									<td className='ordersTableHeaderRowItem dateColumn'>Date</td>
									<td className='ordersTableHeaderRowItem'>Side</td>
									<td className='ordersTableHeaderRowItem'>Type</td>
									<td className='ordersTableHeaderRowItem'>Market</td>
									<td className='ordersTableHeaderRowItem'>Price</td>
									<td className='ordersTableHeaderRowItem'>Amount</td>
									<td className='ordersTableHeaderRowItem'>Filled</td>
									<td className='ordersTableHeaderRowItem'>Remain</td>
									<td className='ordersTableHeaderRowItem'>Status</td>
									<td className='ordersTableHeaderRowItem'>Actions</td>
								</tr>
							</thead>
							<tbody>

								{openOrders
									?
									openOrders.map(m => {
										const priceDecimals = markets[m.market].priceDecimals
										const amountDecimals = markets[m.market].amountDecimals
										const otherCoin = m.market.split('-')[0]
										const baseCoin = m.market.split('-')[1]
										return <tr className='ordersTableBodyRow' key={m.orderId}>
											<td className='ordersTableBodyRowItem'>{Utils.pad(Utils.base32.encode(m.orderId), 6)}</td>
											<td className='ordersTableBodyRowItem'>{m.date ? new Date(m.date).toLocaleString('en-GB') : ''}</td>
											<td className={`ordersTableBodyRowItem sideColumn ${m.side === 'buy' ? 'green' : 'red'}`}>{m.side}</td>
											<td className='ordersTableBodyRowItem typeColumn'>{m.type}</td>
											<td className='ordersTableBodyRowItem'>{m.market}</td>
											<td className='ordersTableBodyRowItem'>{m.type === 'market' ? '-' : `${m.price.toFixed(priceDecimals)} ${baseCoin}`}</td>
											<td className='ordersTableBodyRowItem'>{m.amount.toFixed(amountDecimals)} {otherCoin}</td>
											<td className='ordersTableBodyRowItem'>{m.filled.toFixed(amountDecimals)} {otherCoin}</td>
											<td className='ordersTableBodyRowItem'>{(m.amount - m.filled).toFixed(amountDecimals)} {otherCoin}</td>
											<td className='ordersTableBodyRowItem statusColumn'>{m.filled > 0 ? 'partial' : 'open'}</td>
											<td className='ordersCancelOrder'><span className='ordersCancelOrder pointer' onClick={() => cancelOrder(m.orderId)}>Cancel</span></td>
										</tr>
									})
									:
									<></>
								}

							</tbody>
						</table>
					</div>
					<br /><br />
					<span className='pageBoxTitle'>Orders History</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='ordersTableHeaderRow'>
									<td className='ordersTableHeaderRowItem idColumn'>Order ID</td>
									<td className='ordersTableHeaderRowItem dateColumn'>Date</td>
									<td className='ordersTableHeaderRowItem'>Side</td>
									<td className='ordersTableHeaderRowItem'>Type</td>
									<td className='ordersTableHeaderRowItem'>Market</td>
									<td className='ordersTableHeaderRowItem'>Price</td>
									<td className='ordersTableHeaderRowItem'>Amount</td>
									<td className='ordersTableHeaderRowItem'>Filled</td>
									<td className='ordersTableHeaderRowItem'>Remain</td>
									<td className='ordersTableHeaderRowItem'>Status</td>
								</tr>
							</thead>
							<tbody>

								{ordersHistory
									?
									ordersHistory.map(m => {
										const priceDecimals = markets[m.market].priceDecimals
										const amountDecimals = markets[m.market].amountDecimals
										const otherCoin = m.market.split('-')[0]
										const baseCoin = m.market.split('-')[1]
										return <tr className='ordersTableBodyRow' key={m.orderId}>
											<td className='ordersTableBodyRowItem'>{Utils.pad(Utils.base32.encode(m.orderId), 6)}</td>
											<td className='ordersTableBodyRowItem'>{m.date ? new Date(m.date).toLocaleString('en-GB') : ''}</td>
											<td className={`ordersTableBodyRowItem sideColumn ${m.side === 'buy' ? 'green' : 'red'}`}>{m.side}</td>
											<td className='ordersTableBodyRowItem typeColumn'>{m.type}</td>
											<td className='ordersTableBodyRowItem'>{m.market}</td>
											<td className='ordersTableBodyRowItem'>{m.type === 'market' ? '-' : `${m.price.toFixed(priceDecimals)} ${baseCoin}`}</td>
											<td className='ordersTableBodyRowItem'>{m.amount.toFixed(amountDecimals)} {otherCoin}</td>
											<td className='ordersTableBodyRowItem'>{m.filled.toFixed(amountDecimals)} {otherCoin}</td>
											<td className='ordersTableBodyRowItem'>{(m.amount - m.filled).toFixed(amountDecimals)} {otherCoin}</td>
											<td className='ordersTableBodyRowItem statusColumn'>{m.filled === m.amount ? 'executed' : m.filled > 0 ? 'partial' : 'cancelled'}</td>
										</tr>
									})
									:
									<></>
								}
							</tbody>
						</table>
					</div>
				</TabPanel>
				<TabPanel>
					<span className='pageBoxTitle'>User Ledger</span>
					<div>
						<table className='ordersTableMain'>
							<thead>
								<tr className='historyTableHeaderRow'>
									<td className='historyTableHeaderRowItem dateColumn'>Date</td>
									<td className='historyTableHeaderRowItem descColumn'>Description</td>
									<td className='historyTableHeaderRowItem coinColumn'>Coin</td>
									<td className='historyTableHeaderRowItem'>Value</td>
									<td className='historyTableHeaderRowItem'>Balance after</td>
								</tr>
							</thead>
							<tbody>
								{ledgers.list.length > 0 ?
									ledgers.list.map(ledger =>

										<tr key={ledger.id} className='historyTableBodyRow'>
											<td className='historyTableBodyRowItem'>{new Date(ledger.date).toLocaleString('en-GB')}</td>
											<td className='historyTableBodyRowItem'>{ledger.description}</td>
											<td className='historyTableBodyRowItem'>{ledger.coin}</td>
											<td className={`historyTableBodyRowItem ${ledger.value >= 0 ? `green` : `red`}`}>{ledger.value.toFixed(8)}</td>
											<td className='historyTableBodyRowItem'>{ledger.balance.toFixed(8)}</td>
										</tr>
									)
									:
									<></>
								}

							</tbody>
						</table>
					</div>
				</TabPanel>
				<TabPanel>
					<span className='pageBoxTitle'>Fiat</span>
					<div>
						<table className='walletsTableMain'>
							<thead>
								<tr className='walletsTableHeaderRow'>
									<td className='walletsTableHeaderRowItem coinColumn'>Coin</td>
									<td className='walletsTableHeaderRowItem coinNameColumn'>Name</td>
									<td className='walletsTableHeaderRowItem balanceColumn'>Total</td>
									<td className='walletsTableHeaderRowItem balanceColumn'>Available</td>
									<td className='walletsTableHeaderRowItem balanceColumn'>In Orders</td>
								</tr>
							</thead>
							<tbody>
								<tr className='walletsTableBodyRow'>
									<td className='walletsTableBodyRowItem'><img src={brlLogo} className='walletsLogo' alt='BRL' />BRL</td>
									<td className='walletsTableBodyRowItem'>Brazilian Real</td>
									<td className='walletsTableBodyRowItem'>{walletAmount(wallets.BRL)}</td>
									<td className='walletsTableBodyRowItem'>{walletBalance(wallets.BRL)}</td>
									<td className='walletsTableBodyRowItem'>{walletInOrders(wallets.BRL)}</td>
								</tr>
							</tbody>
						</table>
					</div>
					<br /><br />
					<span className='pageBoxTitle'>Crypto</span>
					<div>
						<table className='walletsTableMain'>
							<thead>
								<tr className='walletsTableHeaderRow'>
									<td className='walletsTableHeaderRowItem coinColumn'>Coin</td>
									<td className='walletsTableHeaderRowItem coinNameColumn'>Name</td>
									<td className='walletsTableHeaderRowItem balanceColumn'>Total</td>
									<td className='walletsTableHeaderRowItem balanceColumn'>Available</td>
									<td className='walletsTableHeaderRowItem balanceColumn'>In Orders</td>
								</tr>
							</thead>
							<tbody>
								<tr className='walletsTableBodyRow'>
									<td className='walletsTableBodyRowItem'><img src={btcLogo} className='walletsLogo' alt='BTC' /> BTC</td>
									<td className='walletsTableBodyRowItem'>Bitcoin</td>
									<td className='walletsTableBodyRowItem'>{walletAmount(wallets.BTC)}</td>
									<td className='walletsTableBodyRowItem'>{walletBalance(wallets.BTC)}</td>
									<td className='walletsTableBodyRowItem'>{walletInOrders(wallets.BTC)}</td>
								</tr>
								<tr className='walletsTableBodyRow'>
									<td className='walletsTableBodyRowItem'><img src={linkLogo} className='walletsLogo' alt='LINK' /> LINK</td>
									<td className='walletsTableBodyRowItem'>Chainlink</td>
									<td className='walletsTableBodyRowItem'>{walletAmount(wallets.LINK)}</td>
									<td className='walletsTableBodyRowItem'>{walletBalance(wallets.LINK)}</td>
									<td className='walletsTableBodyRowItem'>{walletInOrders(wallets.LINK)}</td>
								</tr>
								<tr className='walletsTableBodyRow'>
									<td className='walletsTableBodyRowItem'><img src={ethLogo} className='walletsLogo' alt='ETH' /> ETH</td>
									<td className='walletsTableBodyRowItem'>Ethereum</td>
									<td className='walletsTableBodyRowItem'>{walletAmount(wallets.ETH)}</td>
									<td className='walletsTableBodyRowItem'>{walletBalance(wallets.ETH)}</td>
									<td className='walletsTableBodyRowItem'>{walletInOrders(wallets.ETH)}</td>
								</tr>
								<tr className='walletsTableBodyRow'>
									<td className='walletsTableBodyRowItem'><img src={ebitLogo} className='walletsLogo' alt='EBIT' /> EBIT</td>
									<td className='walletsTableBodyRowItem'>Econobit</td>
									<td className='walletsTableBodyRowItem'>{walletAmount(wallets.EBIT)}</td>
									<td className='walletsTableBodyRowItem'>{walletBalance(wallets.EBIT)}</td>
									<td className='walletsTableBodyRowItem'>{walletInOrders(wallets.EBIT)}</td>
								</tr>
								<tr className='walletsTableBodyRow'>
									<td className='walletsTableBodyRowItem'><img src={ltcLogo} className='walletsLogo' alt='LTC' /> LTC</td>
									<td className='walletsTableBodyRowItem'>Litecoin</td>
									<td className='walletsTableBodyRowItem'>{walletAmount(wallets.LTC)}</td>
									<td className='walletsTableBodyRowItem'>{walletBalance(wallets.LTC)}</td>
									<td className='walletsTableBodyRowItem'>{walletInOrders(wallets.LTC)}</td>
								</tr>
								<tr className='walletsTableBodyRow'>
									<td className='walletsTableBodyRowItem'><img src={usdcLogo} className='walletsLogo' alt='USDC' /> USDC</td>
									<td className='walletsTableBodyRowItem'>USD Coin</td>
									<td className='walletsTableBodyRowItem'>{walletAmount(wallets.USDC)}</td>
									<td className='walletsTableBodyRowItem'>{walletBalance(wallets.USDC)}</td>
									<td className='walletsTableBodyRowItem'>{walletInOrders(wallets.USDC)}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</TabPanel>
			</Tabs>
		</div>
	)
}

const blockExplorer = (coin: string) => {
	const { host } = window.location
	const live = host.includes('econobit.io') && !host.includes('testnet')
	switch (coin) {
		case 'BTC':
			return `https://blockstream.info${live ? '' : '/testnet'}/tx/`
		case 'LTC':
			return `https://chain.so/tx/${live ? 'LTC' : 'LTCTEST'}/`
		default:
			return `https://${live ? '' : 'goerli.'}etherscan.io/tx/`
	}
}



// WEBPACK FOOTER //
// ./src/components/userDetailsMain.tsx